import { Dispatch, SetStateAction } from 'react'
import { FieldValues, UseFormGetValues } from 'react-hook-form'
import { FormItem, IFieldTitleValue, IKeyValue, moduleType } from '../Models/CommonListHeadingModal'
import {
  BuildMasterLayoutContainerObj,
  CheckIsItemInOriginalList,
  CtaDetails,
  DefaultAndModifiedFormStates,
  DisabledContainerIdsInfo,
  FetchSegmentedContainersList,
  GetCategoryFormStatus,
  GetDataCategoryTabs,
  GetKeyToCheck,
  GetSelectedItemList,
  HandleItemConfirmHandler,
  HandleScheduleTimeModal,
  HandleUserUnlockAndNavigate,
  IPinnedItemDetailInRedux,
  MapItemByKey,
  MutateMasterLayoutWidget,
  RemoveAllSelectedItems,
  SelectAllToggle,
  SetDefaultTimeIfEmpty,
  TitleAndLayoutFilterOption,
  ToggleItemPin,
  TransformedObject,
  TypeAndOptionFilter,
  UpdateItemStartAndEndDateTime,
  UpdateSelectedItemModule,
  UploadSiteNavAdditionalParamMedia,
  IUpdateListDataParams,
  GetMasterListingMarkAsDoneStatus,
} from '../Models/DataCategoryUtils'
import {
  addAllKeyValueInList,
  getAssetNodes,
  getConditionalFieldsToShow,
  getContainerTypeList,
  getFieldTitleValues,
  getIdsOfNewlyAddedItems,
  getImageNodeValue,
  getItemIdValue,
  getItemModule,
  getKeyByType,
  getRequiredFields,
  getUpdatedFormFieldValues,
  getWidgetTypeList,
  handleConfigKey,
  handleResponse,
  isEveryItemInListSelected,
  isFieldRequired,
  isItemSelectedOrSaved,
  itemModalConfirmHandler,
  reorderPinnedItem,
  syncUnpinnedItemPositions,
  unlockUser,
  checkIfItemIsSelected,
  handleApiError,
} from './Util'
import { MODULE_CONSTANTS } from '../Constants/Texts'
import { getCheckboxOrRadioComponent } from '../Components/Molecule/CategoryFormData/CommonHelperFunctions'
import {
  setContainerItemDetail,
  setContainerPinnedItemDetail,
  setMasterLayoutItems,
} from '../Store/Slice/manage-container.slice'
import { AppDispatch } from '../Store'
import CONSTANTS, { PAGES_ROUTES } from '../Constants'
import { utcToZonedTime } from 'date-fns-tz'
import { setLoader } from '../Store/Slice/loader.slice'
import { CONTAINER_MANAGEMENT_CRUD } from '../Constants/ApiDefinations'
import mainApiService from '../Services'
import { logger } from './Logger'

const { ASSET_CONFIG, WIDGET_CONFIG, CONTAINER_CONFIG, ASSET } = MODULE_CONSTANTS

export const getFormDefaultAndModifiedValues = <T extends FieldValues>(
  formData: Record<string, any>,
  formTabs: IKeyValue[],
  formDetail: Record<string, any>,
  getValues: UseFormGetValues<T>,
  formStates: DefaultAndModifiedFormStates,
  disabledContainerIdsInfo?: DisabledContainerIdsInfo,
) => {
  if (!formData?.tabs || !formTabs.length) return

  const { setConditionalFieldsToShow, setFormField, setRequiredFields } = formStates
  const requiredFieldsList: string[] = []
  let formFieldValues: { [key: string]: IFieldTitleValue[] } = {}
  const tabsPresent = new Set(formTabs.map((item: IKeyValue) => item.key))

  formData.tabs.forEach((item: Record<string, any>) => {
    const { key } = item
    if (tabsPresent.has(key)) {
      formFieldValues[key] = getFieldTitleValues(item?.tabContent?.formData?.[0]?.form, formDetail)
      item.tabContent?.formData?.forEach((formItem: FormItem) => {
        requiredFieldsList.push(...getRequiredFields(formItem?.form))
      })
    }
  })

  if (formDetail?.parameters?.length > 0) {
    setConditionalFieldsToShow?.(getConditionalFieldsToShow(formData.tabs, getValues()))
    formFieldValues = getUpdatedFormFieldValues(formFieldValues, formDetail.parameters) ?? {
      ...formFieldValues,
    }
  }

  const nonMandatoryFields = disabledContainerIdsInfo?.nonMandatoryFields ?? []

  const filteredRequiredFields =
    nonMandatoryFields.length > 0
      ? requiredFieldsList.filter((field) => !nonMandatoryFields.includes(field))
      : requiredFieldsList

  setFormField?.(formFieldValues)
  setRequiredFields?.(filteredRequiredFields)
}
export const getDataCategoryTabs = ({
  definitionInfo,
  selectedTab,
  setDataSelectedTab,
  setDataTabs,
  setShowTabs,
  displayContainerListingTabs = false,
}: GetDataCategoryTabs): void => {
  if (!definitionInfo) return

  const tabs = definitionInfo?.tabs ?? []

  let filteredTabs: IKeyValue[] = tabs
    .filter(
      (item: any) =>
        item && typeof item.key === 'string' && !item.key.toLowerCase().includes('image'),
    )
    .map((item: any) => ({
      key: item.key,
      value: item.value,
    }))

  if (displayContainerListingTabs) {
    filteredTabs = [...CONSTANTS.MASTER_LISTING_TABS, ...filteredTabs]
  }

  if (filteredTabs.length > 0) {
    setDataTabs(filteredTabs)
    const selectedTabIndex = filteredTabs.findIndex((tab) => tab.key === selectedTab.key)
    if (selectedTabIndex === -1) setDataSelectedTab(filteredTabs[0])
    if (filteredTabs.length > 1) setShowTabs(true)
  }
}
export const getSelectedTabIndex = (
  dataTabs: IKeyValue[],
  selectedTab: IKeyValue,
  setSelectedTabIndex: Dispatch<SetStateAction<number>>,
  isMasterContainerLayout = false,
): void => {
  if (!dataTabs || dataTabs.length === 0 || !selectedTab) return setSelectedTabIndex(1)
  if (isMasterContainerLayout) return setSelectedTabIndex(-1)
  const index = dataTabs.findIndex((tab: IKeyValue) => tab.key === selectedTab.key)
  setSelectedTabIndex(index)
}
export const getCtaDetail = ({
  ctaButtons,
  setKeyToCheckInDefinition,
  setModalSelectedTab,
  setModalTabList,
}: CtaDetails) => {
  if (!ctaButtons?.length) return
  setModalTabList(ctaButtons)
  setModalSelectedTab(ctaButtons[0])
  setKeyToCheckInDefinition(handleConfigKey(ctaButtons[0]))
}
export const handleTypeAndOptionFilter = ({
  definitionInfo,
  keyToCheckInDefinition,
  setIsItemSingleSelect,
  setShouldDisplayRadioFilterOption,
}: TypeAndOptionFilter) => {
  if (!keyToCheckInDefinition || !definitionInfo || Object.keys(definitionInfo).length === 0) {
    setIsItemSingleSelect?.(false)
    return
  }
  setIsItemSingleSelect?.(
    !isFieldRequired(definitionInfo?.[keyToCheckInDefinition]?.isMultiSelection),
  )
  setShouldDisplayRadioFilterOption(
    isFieldRequired(definitionInfo?.[keyToCheckInDefinition]?.isFilterRequired),
  )
}
export const getInitialSelectAllCheckPage = (
  isDialogOpen: boolean,
  tabsList: string[],
  setSelectAllCheckedPageNumber: Dispatch<SetStateAction<Record<string, Set<number>>>>,
): void => {
  if (!isDialogOpen) return
  const updatedSelectAllCheckbox: any = {}
  tabsList.forEach((tab: string) => {
    updatedSelectAllCheckbox[tab] = new Set<number>()
  })
  setSelectAllCheckedPageNumber(updatedSelectAllCheckbox)
}
export const getTitleAndLayoutFilterOption = ({
  assetCategory,
  dispatch,
  keyToCheckInDefinition,
  setRadioOptions,
  setUpdateRadioOptions,
  shouldDisplayRadioFilterOption,
  updateRadioOptions,
}: TitleAndLayoutFilterOption): void => {
  if (!shouldDisplayRadioFilterOption || (shouldDisplayRadioFilterOption && !updateRadioOptions))
    return

  if (keyToCheckInDefinition === ASSET_CONFIG) {
    if (assetCategory.length === 0) return
    setRadioOptions(addAllKeyValueInList(assetCategory))
  } else if (keyToCheckInDefinition === CONTAINER_CONFIG) {
    getContainerTypeList(dispatch).then((response) => setRadioOptions(response))
  } else if (keyToCheckInDefinition === WIDGET_CONFIG) {
    getWidgetTypeList(dispatch).then((response) => setRadioOptions(response))
  }
  setUpdateRadioOptions(false)
}
export const updateItemDetail = (
  data: any,
  checked: boolean,
  idKey: string,
  onChange: (item: any) => void,
  isItemSingleSelect: boolean,
  replaceAndEditItemData?: { id: string },
) => {
  if (!data) return

  const propsData = {
    checked: checked,
    onChange: () => onChange(data?.originalValue),
    disabled: false,
  }
  const radioPropsData = {
    key: data[idKey],
    inputId: data[idKey],
    value: data[idKey],
    ...propsData,
  }

  const variantAndLanguageData: any = {}
  const getIdKeyType = getKeyByType(data, 'id')
  if (getIdKeyType?.toLowerCase().includes('asset')) {
    variantAndLanguageData.variantType = data?.variantType || 'NA'
    variantAndLanguageData.defaultLanguage = data?.defaultLanguage || 'NA'
  }

  return {
    ...data,
    title: data.title || data.widgetTitle || data.containerTitle,
    type: data.assetType || data.type || data.widgetType,
    layout: data.layout || data.widgetLayout || data.containerLayout,
    ...variantAndLanguageData,
    checkbox: replaceAndEditItemData?.id
      ? getCheckboxOrRadioComponent(true, { ...radioPropsData }, {})
      : getCheckboxOrRadioComponent(
          isItemSingleSelect,
          { ...radioPropsData },
          {
            id: data[idKey],
            checked: checked,
            handleChange: () => onChange(data?.originalValue),
            disabled: false,
          },
        ),
  }
}
export const getAllSelectedItemsList = <T extends string = moduleType>(
  itemsList: any[],
  checkedItemList: Record<T, Set<string>>,
  moduleType: T,
) => {
  if (
    !Array.isArray(itemsList) ||
    !itemsList.length ||
    !checkedItemList[moduleType] ||
    !checkedItemList[moduleType].size
  )
    return []
  return itemsList.filter((item: any) => {
    if (typeof item !== 'object' || item === null) {
      return true
    }
    return checkedItemList[moduleType].has(getItemIdValue(item))
  })
}
export const removeAllSelectedItems = <T extends string = moduleType>({
  clearAllItems,
  handleClose,
  itemLists,
  pageModule,
  setCheckedState,
  setSelectedItemListData,
  moduleType,
  checkedItemListRef,
  dispatch,
  pinnedItemPositions,
  isAssetCtaPresent,
  layout,
  containerId,
}: RemoveAllSelectedItems<T>) => {
  const keyToCheck = getKeyToCheck({ layout: layout ?? '', ctaName: moduleType })
  const updatedItemDetail = Array.isArray(itemLists)
    ? { [keyToCheck]: [...itemLists] }
    : { ...itemLists }
  updatedItemDetail[keyToCheck] = updatedItemDetail?.[keyToCheck]?.filter((item: any) => {
    return !checkedItemListRef.current[moduleType]?.has(getItemIdValue(item))
  })

  if (pageModule === 'container') {
    updatedItemDetail.widgetInfo = syncUnpinnedItemPositions(
      updatedItemDetail.widgetInfo ?? [],
      pinnedItemPositions ?? new Set<number>(),
    )
    updatedItemDetail.childPinnedInfo = syncUnpinnedItemPositions(
      updatedItemDetail.childPinnedInfo ?? [],
      pinnedItemPositions ?? new Set<number>(),
    )
    if (isAssetCtaPresent) {
      updatedItemDetail.assetInfo = syncUnpinnedItemPositions(
        updatedItemDetail.assetInfo ?? [],
        new Set<number>(),
      )
    }

    dispatch?.(setContainerItemDetail({ containerId: containerId ?? '', data: updatedItemDetail }))
    if (isMasterContainerLayout(layout ?? '')) {
      dispatch?.(
        setMasterLayoutItems({
          containerId: containerId ?? '',
          data: transformChildPinnedInfo(updatedItemDetail.childPinnedInfo),
        }),
      )
    }
  } else {
    setSelectedItemListData?.(
      syncUnpinnedItemPositions(
        updatedItemDetail[keyToCheck] ?? [],
        pinnedItemPositions ?? new Set<number>(),
      ),
    )
  }
  handleClose()
  setCheckedState({})
  clearAllItems(moduleType)
}
export const handleSelectAllToggle = ({
  checkedState,
  disabledList,
  isItemSingleSelect,
  isSelectAllChecked,
  listData,
  modalSelectedTab,
  newlySelectedItems,
  onSelectChange,
  pageNumber,
  previouslySelectedItems,
  replaceAndEditItemData,
  selectAllCheckedPageNumber,
  setCheckedState,
  setListData,
  setNewlySelectedItems,
  setSelectAllCheckedPageNumber,
  setSelectAllProps,
  setShouldSelectDeselectAllItem,
}: SelectAllToggle) => {
  const isCurrentPageSelected = selectAllCheckedPageNumber?.[modalSelectedTab]?.has?.(pageNumber)

  if (!isSelectAllChecked && !isCurrentPageSelected) {
    setShouldSelectDeselectAllItem(false)
    return
  }

  const updates = {
    checkedState: { ...checkedState },
    selectedList: [...newlySelectedItems],
    listData: [...listData],
    pageNumbers: { ...selectAllCheckedPageNumber },
  }

  const allSelectedWidgets = [...previouslySelectedItems, ...newlySelectedItems]

  updates.listData = updates.listData.map((item) => {
    const itemId = getKeyByType(item, 'id')
    const itemIdValue = item[itemId]
    const isAlreadySelected = isItemSelectedOrSaved(
      isSelectAllChecked ? allSelectedWidgets : previouslySelectedItems,
      itemIdValue,
      newlySelectedItems,
    )

    if (
      (isAlreadySelected && isSelectAllChecked) ||
      (!isSelectAllChecked && disabledList.has(itemIdValue?.toString()))
    )
      return item
    if (isSelectAllChecked) {
      updates.selectedList.push(item.originalValue)
      updates.checkedState[itemIdValue] = true
    } else {
      updates.selectedList = updates.selectedList.filter(
        (selectedItem) => getItemIdValue(selectedItem) !== itemIdValue?.toString(),
      )
      updates.checkedState[itemIdValue] = false
    }

    return updateItemDetail(
      item,
      isSelectAllChecked,
      itemId,
      onSelectChange,
      isItemSingleSelect,
      replaceAndEditItemData,
    )
  })

  if (isSelectAllChecked) {
    updates.pageNumbers[modalSelectedTab]?.add(pageNumber)
  } else {
    updates.pageNumbers[modalSelectedTab]?.delete(pageNumber)
  }

  const isAllItemsSelected = isEveryItemInListSelected(
    [...previouslySelectedItems, ...updates.selectedList],
    updates.listData,
    updates.selectedList,
  )

  setSelectAllProps((prev) => ({ ...prev, checked: isAllItemsSelected }))
  setListData(updates.listData)
  setNewlySelectedItems(updates.selectedList)
  setCheckedState(updates.checkedState)
  setSelectAllCheckedPageNumber(updates.pageNumbers)
  setShouldSelectDeselectAllItem(false)
}
export const extractValueByType = (response: any, type: 'title' | 'id') => {
  const key = getKeyByType(response, type)
  if (key.trim().length === 0) return ''
  const value = response[key]

  if (typeof value === 'string' || typeof value === 'number') return value
  if (typeof value === 'object' && value !== null && 'label' in value && 'value' in value) {
    return value.value
  }
  return ''
}

export const transformResponse = (response: any) => {
  const nodes = Array.from(response?.moduleInfo || [])
  let images: any[] = []
  const title = extractValueByType(response, 'title')
  const id = extractValueByType(response, 'id')
  const module = response?.module || ''
  const excludedNode = ['images', 'widgetmongoid', 'position']
  const state = response?.state
  const itemType = response?.itemType || ''
  const position = response?.position
  const isScheduled = response?.isScheduled
  const availableFrom = response?.availableFrom
  const availableTo = response?.availableTo
  const isPinned = response?.isPinned
  const container = response?.container ?? response?.containerInfo
  const isWidgetTrayRankingEnabled = response?.isWidgetTrayRankingEnabled || false

  const nodeValue = getAssetNodes(response, excludedNode, module === ASSET ? 'assetId' : '')
  if (nodeValue) nodes.push(...nodeValue)

  const assetKeyForEditModal: any = {}
  const pinSchedule: any = {}
  if (module === ASSET) {
    images = getImageNodeValue(response)
    assetKeyForEditModal['supportedAudioLanguages'] = response?.supportedAudioLanguages || []
    assetKeyForEditModal['pinnedLang'] = response?.pinnedLanguage || []
    assetKeyForEditModal['variantType'] = response?.variantType || 'NA'
  }
  if (response?.widgetMongoId) {
    nodes.push({ label: 'ID', value: response.widgetMongoId })
  }
  if (isScheduled) pinSchedule.isScheduled = isScheduled
  if (availableFrom) pinSchedule.availableFrom = availableFrom
  if (availableTo) pinSchedule.availableTo = availableTo

  let replaceItemInfo = {}
  if (response?.replaceItem) replaceItemInfo = { replaceItem: response.replaceItem }

  return {
    nodes,
    images,
    id,
    title,
    module,
    state,
    itemType,
    position,
    isPinned,
    container,
    isWidgetTrayRankingEnabled,
    ...assetKeyForEditModal,
    ...pinSchedule,
    ...replaceItemInfo,
  }
}
export const validateDetailEquality = (
  reduxItemDetail: Record<string, any[]>,
  detailData: Record<string, any[]>,
) => {
  if (!reduxItemDetail || !detailData) return true
  return Object.entries(reduxItemDetail).every(([key, reduxItems]) => {
    const detailItems = detailData?.[key]
    if (!Array.isArray(detailItems)) return false
    if (detailItems.length !== reduxItems.length) return false

    const idField = key === 'assetInfo' ? 'assetId' : 'widgetMongoId'
    return reduxItems.every((item, index) => {
      const detailItem = detailItems[index]
      if (!item?.[idField] || !detailItem?.[idField]) return false
      return item[idField] === detailItem[idField]
    })
  })
}
export const transformChildPinnedInfo = (childPinnedInfo: any[]) => {
  return (childPinnedInfo ?? []).map((item: any) => ({
    ...item,
    container: buildMasterLayoutContainerObj({
      itemList: item.containerInfo,
      modalType: 'noModal',
    }),
  }))
}
export const updateItemDetailInRedux = (
  tabList: string[],
  detailData: Record<string, any>,
  dispatch: AppDispatch,
  layout: string,
  containerId: string,
) => {
  const dataContentInfo: { [key: string]: any[] } = {}
  const pinnedItemsInfo: IPinnedItemDetailInRedux = {
    ids: [],
    position: [],
    items: [],
  }
  const unpinnedItemLists: any[] = []
  tabList.forEach((tabTitle) => {
    const keyToCheck = getKeyToCheck({ layout, ctaName: tabTitle })
    if (tabTitle === 'widget' && !isMasterContainerLayout(layout)) {
      detailData?.widgetInfo?.forEach((element: any) => {
        if (element?.isPinned) {
          pinnedItemsInfo.items.push(element)
          pinnedItemsInfo.ids.push(getItemIdValue(element))
          pinnedItemsInfo.position.push(element.position)
        } else {
          unpinnedItemLists.push(element)
        }
      })
      dataContentInfo[keyToCheck] = unpinnedItemLists
    } else {
      dataContentInfo[keyToCheck] = detailData?.[keyToCheck] || []
    }
  })

  dispatch(setContainerPinnedItemDetail({ containerId, data: pinnedItemsInfo }))
  if (isMasterContainerLayout(layout)) {
    const transformedRecords = transformChildPinnedInfo(dataContentInfo?.childPinnedInfo)
    dataContentInfo.childPinnedInfo = transformedRecords
    dispatch(setMasterLayoutItems({ containerId, data: transformedRecords }))
  }
  dispatch(setContainerItemDetail({ containerId, data: dataContentInfo }))
}
const getMarkDoneStatus = (detailData: Record<string, any>) => {
  if (detailData?.markAsDone === '1') {
    return CONSTANTS.MARK_DONE_STATUS.PASS
  }
  if (detailData?.markAsDone === '0') {
    return CONSTANTS.MARK_DONE_STATUS.FAIL
  }
  return CONSTANTS.MARK_DONE_STATUS.NO_ACTION
}
export const caseHandler = (
  key: string,
  tollgatesAvailable: any,
  pageType: string,
  roleDataPermission: any,
  detailData: any,
  formValidStatus: boolean,
) => {
  const resultData = {
    status: CONSTANTS.MARK_DONE_STATUS.NO_ACTION,
    disabled: true,
    error: null,
    isFormValid: false,
  }

  if (pageType === CONSTANTS.PAGE_TYPE.CREATE) return resultData
  const detailStatus = getMarkDoneStatus(detailData[key])
  if (key) {
    if (tollgatesAvailable) {
      if (
        !roleDataPermission[key]?.canSkipValidation &&
        !roleDataPermission[key]?.canStateTransition
      ) {
        // Code for case when tollgatesAvailable is true and both canSkipValidation and canStateTransition are false
        resultData.status = detailStatus
        resultData.disabled = true
        resultData.error = null
        resultData.isFormValid = formValidStatus
      } else if (
        !roleDataPermission[key]?.canSkipValidation &&
        roleDataPermission[key]?.canStateTransition
      ) {
        // Code for case when tollgatesAvailable is true and canSkipValidation is false and canStateTransition is true
        resultData.status = detailStatus
        resultData.disabled = false
        resultData.error = null
        resultData.isFormValid = formValidStatus
      } else if (
        roleDataPermission[key]?.canSkipValidation &&
        !roleDataPermission[key]?.canStateTransition
      ) {
        // Code for case when tollgatesAvailable is true and canSkipValidation is true and canStateTransition is false
        resultData.status = detailStatus
        resultData.disabled = false
        resultData.error = null
        resultData.isFormValid = true
      }
    } else {
      if (
        !roleDataPermission[key]?.canSkipValidation &&
        !roleDataPermission[key]?.canStateTransition
      ) {
        // Code for case when tollgatesAvailable is false and both canSkipValidation and canStateTransition are false
        resultData.status = detailStatus
        resultData.disabled = false
        resultData.error = null // 'You donâ€™t have the permission to change the state'
      } else if (
        roleDataPermission[key]?.canSkipValidation ||
        roleDataPermission[key]?.canStateTransition
      ) {
        // Code for case when tollgatesAvailable is false and either canSkipValidation or canStateTransition is true
        resultData.status = detailStatus
        resultData.disabled = false
        resultData.error = null
        resultData.isFormValid = true
      }
    }
  }

  return resultData
}
export const getCategoryFormStatus = ({
  definitionDetail,
  formValidStatus,
  key,
  markAsDoneData,
  roleDataPermission,
  setFormValidStatus,
  setMarkAsDoneData,
  value,
}: GetCategoryFormStatus) => {
  const tollgates = definitionDetail?.tollgates?.map((item: string) => item?.toLowerCase())
  let enableMarkAsDone = false

  if (tollgates?.includes(key)) {
    enableMarkAsDone =
      roleDataPermission[key]?.canSkipValidation && !roleDataPermission[key]?.canStateTransition
  } else {
    enableMarkAsDone =
      roleDataPermission[key]?.canSkipValidation || roleDataPermission[key]?.canStateTransition
  }

  if (!enableMarkAsDone) {
    setMarkAsDoneData((prevData: typeof markAsDoneData) => {
      const oldCategoryData = prevData[key] || {}
      if (oldCategoryData.isFormValid !== value) {
        return {
          ...prevData,
          [key]: {
            ...oldCategoryData,
            isFormValid: value,
          },
        }
      }
      return prevData
    })
    if (formValidStatus !== value) {
      setFormValidStatus(value)
    }
  }
}
export const handleUserUnlockAndNavigate = ({
  isLayoutLocked,
  layoutDetailData,
  location,
  navigate,
  navigateUrl,
  pageModule,
  selectedCategoryKey,
  layoutId,
  pageType,
}: HandleUserUnlockAndNavigate) => {
  if (isLayoutLocked || pageType !== CONSTANTS.PAGE_TYPE.EDIT) return
  unlockUser(layoutDetailData, pageModule.toLowerCase(), `${pageModule}Id`)
  navigate(
    `/${navigateUrl}/${PAGES_ROUTES.PageTypeView}/${layoutId}?category=${selectedCategoryKey}`,
    { replace: true, state: { ...location.state } },
  )
}
export const uploadSiteNavAdditionalParamMedia = async ({
  file,
  imageFieldsData,
  parameter,
  setImageFieldsData,
  val,
}: UploadSiteNavAdditionalParamMedia) => {
  if (!parameter || Object.keys(parameter).length === 0) return false

  const { _id, parameterName, fieldName } = parameter
  const oldImageFieldsData = [...imageFieldsData]
  if (val.trim().length === 0 || !file || typeof file === 'string') {
    const updatedImageFieldsData = oldImageFieldsData.filter(
      (item: any) => item?.fieldName !== fieldName,
    )
    setImageFieldsData(updatedImageFieldsData)
    return true
  }

  const { aspectRatio, resolution, mimeType } = file

  const imageData = {
    fieldName,
    parameterId: _id,
    image: {
      s3Url: `${CONSTANTS.S3_BUCKET_BASE_URL}/${val}`,
      resolution: resolution,
      aspectRatio: aspectRatio,
      imageNode: parameterName,
      mimeType: mimeType,
    },
    value: '',
  }
  oldImageFieldsData.push(imageData)
  setImageFieldsData(oldImageFieldsData)
  return true
}
export const handleSiteNavAdditionalParamMedia = async ({
  file,
  imageFieldsData,
  parameter,
  setImageFieldsData,
  val,
}: UploadSiteNavAdditionalParamMedia) => {
  const result = await uploadSiteNavAdditionalParamMedia({
    file,
    imageFieldsData,
    parameter,
    setImageFieldsData,
    val,
  })
  return result === true
}
export const transformObject = (obj: any): TransformedObject => {
  return {
    parameterId: obj?.parameterId?._id,
    fieldName: obj?.fieldName,
    value: obj?.value,
    ...(obj?.image && { image: obj.image }),
    _id: obj?._id,
  }
}
export const updateItemStartAndEndDateTime = ({
  selectedItemListData,
  setSelectedItemListData,
  updatedItem,
}: UpdateItemStartAndEndDateTime) => {
  if (
    selectedItemListData.length === 0 ||
    !updatedItem ||
    typeof updatedItem !== 'object' ||
    (typeof updatedItem === 'object' && Object.keys(updatedItem).length === 0)
  )
    return selectedItemListData

  const clonedItemList = structuredClone(selectedItemListData)
  const updatedItemIdValue = getItemIdValue(updatedItem)
  const filteredItem = clonedItemList.filter(
    (item: any) => getItemIdValue(item) === updatedItemIdValue,
  )

  if (filteredItem.length) {
    const itemToUpdate = filteredItem[0]

    itemToUpdate.isScheduled = updatedItem.isScheduled

    if (updatedItem.availableFrom) {
      itemToUpdate.availableFrom = updatedItem.availableFrom
    } else {
      delete itemToUpdate.availableFrom
    }

    if (updatedItem.availableTo) {
      itemToUpdate.availableTo = updatedItem.availableTo
    } else {
      delete itemToUpdate.availableTo
    }
  }
  setSelectedItemListData?.(clonedItemList)
  return clonedItemList
}
export const setDefaultTimeIfEmpty = ({ offsetMinutes, value, setter }: SetDefaultTimeIfEmpty) => {
  if (value) {
    setter(value)
    return value
  } else {
    const newDate = new Date()
    newDate.setSeconds(0)
    newDate.setMilliseconds(0)
    newDate.setMinutes(newDate.getMinutes() + offsetMinutes)
    setter(newDate)
    return newDate
  }
}
export const convertUTCToLocal = (utcDateString: string): Date | null => {
  try {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const localDate = utcToZonedTime(utcDateString, userTimeZone)
    localDate.setSeconds(0)
    localDate.setMilliseconds(0)
    return localDate
  } catch (error) {
    logger.error(error)
    return null
  }
}
export const transformItemInfo = (item: any, idRequired = true) => {
  return {
    ...(idRequired ? { id: getItemIdValue(item) } : {}),
    isPinned: item?.isPinned,
    ...(item?.isScheduled ? { isScheduled: item.isScheduled } : {}),
    ...(item?.availableFrom ? { availableFrom: item.availableFrom } : {}),
    ...(item?.availableTo ? { availableTo: item.availableTo } : {}),
  }
}
export const transformMasterContainerInfo = (
  item: any,
  layout: string,
  includeActionKey = true,
) => {
  if (!isMasterContainerLayout(layout)) return {}
  const containers = item?.container ?? item?.containerInfo ?? []
  const objKeyToCheck = item?.container ? 'key' : 'containerId'
  const containerDetail = containers.map((res: any) => ({
    _id: res._id,
    containerId: res[objKeyToCheck],
    position: res.position,
    ...(res?.action && includeActionKey ? { action: res.action } : {}),
  }))
  return {
    container: containerDetail,
  }
}
export const transformContentInfoArray = (itemLists: any[], idRequired = true) => {
  return itemLists.map((item: any) => transformItemInfo(item, idRequired))
}
export const generateScheduledPayload = (item: any) => {
  return {
    isScheduled: item?.isScheduled ?? false,
    ...(item?.availableFrom ? { availableFrom: item.availableFrom } : {}),
    ...(item?.availableTo ? { availableTo: item.availableTo } : {}),
  }
}
export const transformArrayToObject = (targetedArray: any) => {
  return {
    contentInfo: transformContentInfoArray(targetedArray?.contentInfo ?? []),
    parameters:
      targetedArray?.parameters?.reduce((acc: any, param: any) => {
        acc[param.fieldName] = param.value
        return acc
      }, {}) ?? {},
  }
}
export const mapItemByKey = ({
  data,
  dispatch,
  itemDetailFromRedux,
  module,
  pinnedItemPositions,
  layout,
  containerId,
}: MapItemByKey) => {
  const itemListInRedux = { ...itemDetailFromRedux }
  const infoKey = getKeyToCheck({ layout, ctaName: module })
  const selectedItemList = [...(itemListInRedux?.[infoKey] || [])]
  const shuffledItemArray = data.map((itemFromSecondArray) => {
    const itemId = itemFromSecondArray?.id?.toString()
    return selectedItemList.find((itemFromFirstArray: any) => {
      const idKey = getKeyByType(itemFromFirstArray, 'id')
      const selectedId = itemFromFirstArray?.[idKey]?.toString()
      const contentId = itemFromFirstArray?.contentId?.toString()
      return selectedId === itemId || contentId === itemId
    })
  })
  itemListInRedux[infoKey] = syncUnpinnedItemPositions(shuffledItemArray, pinnedItemPositions)
  dispatch(setContainerItemDetail({ containerId: containerId ?? '', data: itemListInRedux }))
}
export const toggleItemPin = ({
  containerDispatchSetter,
  data,
  dispatch,
  dispatchSetter,
  itemLists,
  pinLayoutType = 'widget',
  reduxPinnedItemsInfo,
  setter,
  type,
}: ToggleItemPin) => {
  const updatedIds: Set<string> = new Set(reduxPinnedItemsInfo.ids)
  const updatedPositions: Set<number> = new Set(reduxPinnedItemsInfo.position)
  let updatedItems = [...reduxPinnedItemsInfo.items]
  const itemsToUse = Array.isArray(itemLists)
    ? itemLists
    : ((itemLists as Record<string, any[]>)?.['widgetInfo'] ?? [])
  let updatedSelectedItems = [...itemsToUse]

  const idValue = getItemIdValue(data)
  if (type === 'remove') {
    updatedIds.delete(idValue)
    updatedItems = updatedItems.filter((item) => getItemIdValue(item) !== idValue)
    const reAddItems = reduxPinnedItemsInfo.items
      .filter((item) => getItemIdValue(item) === idValue)
      .map((item) => {
        const itemToUpdate = { ...item }
        delete itemToUpdate?.availableTo
        delete itemToUpdate?.availableFrom
        return { ...itemToUpdate, isPinned: false, isScheduled: false }
      })

    updatedSelectedItems = reorderPinnedItem(updatedSelectedItems, reAddItems)

    updatedPositions.delete(reAddItems[0]?.position)
  } else if (type === 'pin') {
    updatedIds.add(idValue)
    const toPinItems = updatedSelectedItems
      .filter((item) => getItemIdValue(item) === idValue)
      .map((item) => ({ ...item, isPinned: true }))
    updatedItems = reorderPinnedItem(updatedItems, toPinItems)
    updatedSelectedItems = updatedSelectedItems.filter((item) => getItemIdValue(item) !== idValue)
    updatedPositions.add(toPinItems[0]?.position)
  }

  if (type !== 'update') {
    const syncedData = syncUnpinnedItemPositions(updatedSelectedItems, updatedPositions)
    if (pinLayoutType === 'widget') {
      setter?.(syncedData)
    } else if (
      pinLayoutType === 'container' &&
      !Array.isArray(itemLists) &&
      containerDispatchSetter
    ) {
      const dataToUpdate = { ...itemLists }
      dataToUpdate.widgetInfo = syncedData
      dispatch(containerDispatchSetter(dataToUpdate))
    }
  }

  const result: IPinnedItemDetailInRedux = {
    ids: [...updatedIds],
    items: type === 'update' ? updatedSelectedItems : updatedItems,
    position: [...updatedPositions],
  }

  dispatch(dispatchSetter(result))
}
export const updateSelectedItemModule = ({
  item,
  setSelectedItemModule,
}: UpdateSelectedItemModule) => {
  const itemModule = getItemModule(item)
  setSelectedItemModule(typeof itemModule !== 'undefined' ? (itemModule as moduleType) : undefined)
}
export const handleScheduleTimeModal = ({
  id,
  item,
  setScheduleItemModal,
  setSelectedItemModule,
}: HandleScheduleTimeModal) => {
  setScheduleItemModal((prev) => ({
    ...prev,
    id,
    open: true,
  }))
  updateSelectedItemModule({ item, setSelectedItemModule })
}
export const handleItemConfirmHandler = async ({
  currentStep,
  dispatch,
  handleEditParameterBtnClick,
  modalTabList,
  newlySelectedItems,
  onSuccessResponse,
  pageType,
  replaceAndAddedItem,
  replaceAndEditItemData,
  selectedItemListData,
  setAdditionalParameterItemId,
  setBaseErrorIds,
  setBaseErrorIdsType,
  setClearFilterValues,
  setCurrentStep,
  setDisplayBackCta,
  setEditItemDetail,
  setIsDialogOpen,
  setItemsLayoutList,
  setKeyToCheckInDefinition,
  setLastSearchedText,
  setListData,
  setModalSelectedTab,
  setModalTitleBasedOnLayout,
  setNewlySelectedItems,
  setRadioOptions,
  setReplaceAndAddedItem,
  setReplaceAndEditItemData,
  setSelectedFilterValues,
  setSelectedItemModule,
  setUpdateRadioOptions,
  additionalParameterItemId,
  getItemDetail,
  modalType,
  pinnedItemPositions,
  layout = '',
}: HandleItemConfirmHandler) => {
  const { VIEW } = CONSTANTS.PAGE_TYPE
  if (additionalParameterItemId?.trim()?.length && pageType === VIEW) {
    handleEditParameterBtnClick?.()
    return
  }
  if (currentStep < 2) {
    setCurrentStep(currentStep + 1)
    setLastSearchedText('')
    setClearFilterValues(true)
    setSelectedFilterValues([])
    setItemsLayoutList([])
    setModalSelectedTab?.(modalTabList?.[0])
    setRadioOptions?.([])
    setKeyToCheckInDefinition?.(handleConfigKey(modalTabList?.[0]))
    setUpdateRadioOptions?.(true)
    setListData([])
  } else if (isMasterContainerLayout(layout) && currentStep === 2) {
    setCurrentStep(3)
    if (replaceAndEditItemData.type === 'edit') setDisplayBackCta(true)
  } else {
    const response = await itemModalConfirmHandler({
      replaceAndEditItemData,
      newlySelectedItems,
      selectedItemListData,
      dispatch,
      setBaseErrorIds,
      additionalParameterItemId,
      pinnedItemPositions,
      modalType,
      setBaseErrorIdsType,
    })
    if (response) {
      if (!additionalParameterItemId?.trim()?.length) {
        let finalResult = response
        if (isMasterContainerLayout(layout)) {
          const mapContainerListById = new Map(
            newlySelectedItems.map((item: any) => [getItemIdValue(item), item.container]),
          )
          const now = new Date().toISOString()

          finalResult = finalResult.map((responseItem: any) => {
            const itemId = getItemIdValue(responseItem)

            const baseContainer = mapContainerListById.get(itemId) ?? responseItem.container

            const shouldReplaceContainer =
              replaceAndEditItemData.type === 'replace' &&
              newlySelectedItems[0]?.replaceItem &&
              itemId === getItemIdValue(newlySelectedItems[0])

            return {
              ...responseItem,
              isPinned: true,
              isScheduled: true,
              availableFrom: responseItem?.availableFrom ?? now,
              ...(shouldReplaceContainer ? { replaceItem: newlySelectedItems[0].replaceItem } : {}),
              container: shouldReplaceContainer
                ? (newlySelectedItems[0]?.container ?? []).map((item: any) => ({
                    ...item,
                    action: 'replace',
                  }))
                : baseContainer,
            }
          })
        }
        onSuccessResponse(finalResult)
        setReplaceAndAddedItem(getIdsOfNewlyAddedItems(newlySelectedItems, replaceAndAddedItem))
      }
      getItemDetail?.()
      setBaseErrorIds([])
      setBaseErrorIdsType?.('')
      setReplaceAndEditItemData({ id: '', position: 0, type: 'add' })
      setNewlySelectedItems([])
      setIsDialogOpen(false)
      setCurrentStep(1)
      setAdditionalParameterItemId?.('')
      setDisplayBackCta(true)
      setSelectedItemModule(undefined)
      setEditItemDetail(undefined)
      setModalTitleBasedOnLayout?.('')
      setItemsLayoutList([])
    }
  }
}
export const getSelectedItemList = ({
  layout,
  modalSelectedTab,
  pinnedItems,
  selectedItemDetail,
}: GetSelectedItemList) => {
  let selectedItemsList =
    selectedItemDetail?.[getKeyToCheck({ layout, ctaName: modalSelectedTab })] || []
  if (modalSelectedTab === 'widget' && !isMasterContainerLayout(layout))
    selectedItemsList = [...selectedItemsList, ...pinnedItems]
  return selectedItemsList
}
export const isAssetCtaAvailable = (definitionInfo: Record<string, any[]>) => {
  return (definitionInfo?.ctaButton ?? []).includes('asset')
}
export const isMasterContainerLayout = (layout: string) => {
  return CONSTANTS.SHOW_CONTAINER_LISTING_TABS.includes(layout.toLowerCase())
}
export const isSegmentedLayoutContainer = (layout: string) => {
  return CONSTANTS.INCLUDE_LAYOUTS_CHILD_RIGHT_DRAWER.includes(layout.toLowerCase())
}
export const getKeyToCheck = ({ ctaName, layout }: GetKeyToCheck) => {
  const prefix = isMasterContainerLayout(layout) ? 'childPinned' : ctaName
  return `${prefix}Info`
}
export const getMasterLayoutContainerObjToValidate = (item: {
  containerId?: number
  position: number
  _id: string
  key?: number
}) => {
  const { _id, containerId, position, key } = item
  return {
    _id,
    containerId: containerId ?? key,
    position,
  }
}
export const mutateMasterLayoutWidget = ({
  currentSelectedItems,
  prevSelectedItems,
}: MutateMasterLayoutWidget) => {
  const result: any[] = []

  const prevItemsMap = new Map(prevSelectedItems.map((item: any) => [getItemIdValue(item), item]))
  const currentItemsMap = new Map(
    currentSelectedItems.map((item: any) => [getItemIdValue(item), item]),
  )
  const replacedOldItemIds = new Set<string>()

  currentSelectedItems.forEach((currentParentItem: any) => {
    const currentParentId = getItemIdValue(currentParentItem)
    const oldParentItem = prevItemsMap.get(currentParentId)
    prevItemsMap.delete(currentParentId)

    if (currentParentItem?.replaceItem) {
      const replacedId = String(currentParentItem.replaceItem.widgetId)
      replacedOldItemIds.add(replacedId)
      result.push({
        ...currentParentItem,
        container: currentParentItem.container.map((item: any) => ({
          ...getMasterLayoutContainerObjToValidate(item),
          action: 'replace',
        })),
      })
      return
    }
    const currentParentContainer = currentParentItem?.container || []

    if (!oldParentItem) {
      result.push({
        ...currentParentItem,
        container: currentParentContainer.map((item: any) => ({
          ...getMasterLayoutContainerObjToValidate(item),
          action: 'add',
        })),
      })
      return
    }

    const containers = oldParentItem?.container ?? oldParentItem?.containerInfo ?? []
    const containerInfoList = oldParentItem?.containerInfo ?? []
    const oldContainerMap = new Map(containers.map((item: any) => [item?._id, item]))
    const containerInfoMap = new Map<string, any>(
      containerInfoList.map((item: any) => [item?._id, item]),
    )

    const updatedContainer: any[] = []

    currentParentContainer.forEach((newItem: any) => {
      const newItemId = newItem?._id
      const oldContainerItem: any = oldContainerMap.get(newItemId)

      const currentContainerObj = getMasterLayoutContainerObjToValidate(newItem)
      if (oldContainerItem) {
        const oldContainerObj = getMasterLayoutContainerObjToValidate(oldContainerItem)

        const isSameContainerObj =
          JSON.stringify(oldContainerObj) === JSON.stringify(currentContainerObj)
        if (isSameContainerObj) {
          const containerInfoItem = containerInfoMap.get(newItemId)
          updatedContainer.push({
            ...currentContainerObj,
            action: containerInfoItem?.action ?? 'add',
          })
        } else {
          updatedContainer.push({
            ...currentContainerObj,
            action: 'edit',
          })
        }

        oldContainerMap.delete(newItemId)
      } else {
        updatedContainer.push({ ...currentContainerObj, action: 'add' })
      }
    })

    oldContainerMap.forEach((oldItem: any) => {
      updatedContainer.push({
        ...getMasterLayoutContainerObjToValidate(oldItem),
        action: 'remove',
      })
    })

    result.push({
      ...currentParentItem,
      container: updatedContainer,
    })
  })

  prevSelectedItems.forEach((oldParent: any) => {
    const oldId = getItemIdValue(oldParent)
    if (!currentItemsMap.has(oldId) && !replacedOldItemIds.has(oldId)) {
      result.push({
        ...oldParent,
        container: (oldParent.container || []).map((item: any) => ({
          ...getMasterLayoutContainerObjToValidate(item),
          action: 'remove',
        })),
      })
    }
  })

  return result
}
export const buildMasterLayoutContainerObj = ({
  itemList,
  modalType,
  prevSelectedItemList,
}: BuildMasterLayoutContainerObj) => {
  if (!itemList || !Array.isArray(itemList)) return []
  let mapPrevItemContainers = new Map()

  if (
    modalType === 'edit' &&
    prevSelectedItemList &&
    Array.isArray(prevSelectedItemList?.[0]?.container)
  ) {
    mapPrevItemContainers = new Map(
      prevSelectedItemList[0].container.map((item: any) => [item?.key, item]),
    )
  }
  return itemList.map((item: any) => {
    let count = item?.count ?? item?.widgetInfoLength ?? 0
    if (modalType === 'edit' && mapPrevItemContainers.has(item?.containerId))
      count = mapPrevItemContainers.get(item.containerId).count
    if (modalType !== 'noModal') count += 1
    return {
      _id: item._id,
      key: item?.containerId,
      value: item?.title ?? item?.value ?? '',
      count,
      position: item?.position ?? count,
    }
  })
}
export const fetchSegmentedContainersList = async ({
  dispatch,
  parentContainerId,
  modalType,
  prevSelectedItemList,
}: FetchSegmentedContainersList) => {
  const payload: any = {
    filters: [
      {
        filterBy: 'state',
        filterCondition: 'in',
        filterValue: ['featured', 'scheduled_to_feature'],
      },
      {
        filterBy: 'isTrayRankingEnabled',
        filterCondition: 'equals',
        filterValue: true,
      },
    ],
    sortType: -1,
    page: 1,
    limit: 10,
    parentContainerId,
  }

  dispatch(setLoader(true))
  const requestedData = CONTAINER_MANAGEMENT_CRUD.CHILD_LISTING(payload)
  const result: any = await mainApiService(requestedData)
  const { responseCode } = handleResponse(result)
  let containerList: any[] = []
  if (responseCode === 200) {
    containerList = buildMasterLayoutContainerObj({
      itemList: result?.data?.records,
      modalType,
      prevSelectedItemList,
    })
  }
  dispatch(setLoader(false))
  return containerList
}
export const buildSegmentedListRows = (data: any) => {
  if (!data?.container || !Array.isArray(data.container)) return data
  const { container, ...parentObj } = data

  return container.slice(1).map((item: any) => ({
    ...parentObj,
    container: [item],
  }))
}
export const checkIsItemInOriginalList = ({
  layout,
  originalList,
  replaceAndEditItemData,
}: CheckIsItemInOriginalList) => {
  if (
    !isMasterContainerLayout(layout) ||
    replaceAndEditItemData.type !== 'replace' ||
    !Array.isArray(originalList)
  )
    return false
  return originalList.some(
    (item: any) => getItemIdValue(item) === replaceAndEditItemData.id.toString(),
  )
}

export const transformSegmentedResponse = (response: any) => {
  const nodes = Array.from(response?.moduleInfo || [])
  const title = extractValueByType(response, 'title')
  const id = extractValueByType(response, 'id')
  const module = response?.module || ''
  const excludedNode = ['images', 'containerMongoId', 'position']
  const state = response?.state
  const itemType = response?.itemType || ''
  const position = response?.position

  const nodeValue = getAssetNodes(response, excludedNode, module === ASSET ? 'assetId' : '')
  if (nodeValue) nodes.push(...nodeValue)

  if (response?.containerMongoId) {
    nodes.push({ label: 'ID', value: response.containerMongoId })
  }
  return {
    nodes,
    id,
    title,
    module,
    state,
    itemType,
    position,
  }
}
export const updateListData = ({
  itemId,
  checked,
  idKey,
  isReplace = false,
  listData,
  checkedState,
  selectedItemListData,
  onSelectChange,
  isItemSingleSelect,
  replaceAndEditItemData,
  listSetter,
}: IUpdateListDataParams): void => {
  const updatedListData = [...listData]
  updatedListData.forEach((item, index) => {
    const IDKey = getKeyByType(item, 'id')
    if (item[IDKey] !== itemId) {
      if (isReplace && checkedState[item[IDKey]]) {
        const isItemSelected = checkIfItemIsSelected(selectedItemListData, item[IDKey])
        if (!isItemSelected)
          updatedListData[index] = updateItemDetail(
            item,
            false,
            IDKey,
            onSelectChange,
            isItemSingleSelect,
            replaceAndEditItemData,
          )
      } else if (isItemSingleSelect && !isReplace) {
        updatedListData[index] = updateItemDetail(
          item,
          false,
          IDKey,
          onSelectChange,
          isItemSingleSelect,
          replaceAndEditItemData,
        )
      }
    }
    if (item[IDKey] === itemId) {
      updatedListData[index] = updateItemDetail(
        item,
        checked,
        idKey,
        onSelectChange,
        isItemSingleSelect,
        replaceAndEditItemData,
      )
    }
  })
  listSetter(updatedListData)
}
export const getMasterListingMarkAsDoneStatus = async ({
  dispatch,
  validationId,
}: GetMasterListingMarkAsDoneStatus) => {
  const response = { shouldStopPolling: false, reFetchDetail: false }
  if (!validationId) return response

  const { MASTER_LISTING_VALIDATION_STATUS } = CONSTANTS
  const { FAILED, PASSED, PARTIAL_FAILED, IN_PROGRESS } = MASTER_LISTING_VALIDATION_STATUS

  let result: any = {}
  let isValidationPassed = false
  let isValidationFailed = false
  let isValidationInProgess = false

  try {
    const requestedData = CONTAINER_MANAGEMENT_CRUD.MASTER_LISTING_MARK_AS_DONE_STATUS(validationId)
    result = await mainApiService(requestedData)
    isValidationPassed = result?.data?.validationStatus === PASSED
    isValidationFailed =
      result?.data?.validationStatus === PARTIAL_FAILED || result?.data?.validationStatus === FAILED
    isValidationInProgess = result?.data?.validationStatus === IN_PROGRESS

    if (isValidationFailed) {
      const validationError = {
        error: {
          errors: result?.data?.validationErrors,
        },
      }
      handleApiError(dispatch, validationError)
    }

    return {
      shouldStopPolling: !isValidationInProgess,
      reFetchDetail: isValidationPassed,
    }
  } catch (error) {
    logger.error(error)
    return isValidationFailed
      ? { shouldStopPolling: true, reFetchDetail: isValidationPassed }
      : response
  }
}
